/** Presentation helpers. Everything tolerates null so callers stay terse. */

const MINUTE = 60_000
const HOUR = 60 * MINUTE
const DAY = 24 * HOUR

export function parseDate(value: string | number | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date
}

/** "just now" / "4m ago" / "3d ago" — falls back to a date past a week. */
export function relativeTime(value: string | number | null | undefined, now = Date.now()): string {
  const date = parseDate(value)
  if (!date) return 'unknown'

  const diff = now - date.getTime()
  const abs = Math.abs(diff)
  const suffix = diff >= 0 ? 'ago' : 'from now'

  if (abs < 45_000) return diff >= 0 ? 'just now' : 'in a moment'
  if (abs < HOUR) return `${Math.round(abs / MINUTE)}m ${suffix}`
  if (abs < DAY) return `${Math.round(abs / HOUR)}h ${suffix}`
  if (abs < 7 * DAY) return `${Math.round(abs / DAY)}d ${suffix}`
  return formatDate(date)
}

export function formatDate(value: string | number | Date | null | undefined): string {
  const date = value instanceof Date ? value : parseDate(value ?? null)
  if (!date) return '—'
  return date.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })
}

export function formatDateTime(value: string | number | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '—'
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

/** For `<input type="date">`, which insists on YYYY-MM-DD. */
export function toDateInputValue(value: string | number | null | undefined): string {
  const date = parseDate(value)
  if (!date) return ''
  const month = `${date.getMonth() + 1}`.padStart(2, '0')
  const day = `${date.getDate()}`.padStart(2, '0')
  return `${date.getFullYear()}-${month}-${day}`
}

/** Local date input -> ISO instant at end of that day, so expiry is inclusive. */
export function fromDateInputValue(value: string): string | null {
  if (!value) return null
  const date = new Date(`${value}T23:59:59`)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

const UNITS = ['B', 'KB', 'MB', 'GB', 'TB'] as const

export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes <= 0) return '—'
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < UNITS.length - 1) {
    value /= 1024
    unit += 1
  }
  const label = UNITS[unit] ?? 'B'
  return `${value >= 10 || unit === 0 ? Math.round(value) : value.toFixed(1)} ${label}`
}

/** *arr ETAs are seconds remaining; null means the queue has no estimate yet. */
export function formatEta(seconds: number | null | undefined): string | null {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds) || seconds <= 0) {
    return null
  }
  if (seconds < 90) return 'less than a minute'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${minutes} min`
  const hours = Math.floor(minutes / 60)
  const rest = minutes % 60
  if (hours < 24) return rest ? `${hours}h ${rest}m` : `${hours}h`
  const days = Math.floor(hours / 24)
  return `${days}d ${hours % 24}h`
}

export function clampPercent(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) return 0
  return Math.min(100, Math.max(0, value))
}

export function formatPercent(value: number | null | undefined): string {
  return `${Math.round(clampPercent(value))}%`
}

/** Turns a stored user agent into something a human can recognise. */
export function describeUserAgent(userAgent: string | null | undefined): string {
  if (!userAgent) return 'Unknown device'
  const ua = userAgent
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /OPR\//.test(ua) ? 'Opera'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Safari\//.test(ua) ? 'Safari'
    : 'Browser'
  const platform =
    /iPhone|iPad|iPod/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
    : /Mac OS X/.test(ua) ? 'macOS'
    : /Windows/.test(ua) ? 'Windows'
    : /Linux/.test(ua) ? 'Linux'
    : 'Unknown OS'
  return `${browser} on ${platform}`
}

export function inviteUrl(code: string, base?: string | null): string {
  const origin = base ?? (typeof window === 'undefined' ? '' : window.location.origin)
  return `${origin}/invite/${code}`
}

const SERVICE_LABELS: Record<string, string> = {
  jellyfin: 'Jellyfin',
  seerr: 'Requests',
  radarr: 'Movies',
  sonarr: 'TV',
  qbittorrent: 'Downloads',
  live: 'Live sports',
  worker: 'Portal',
  db: 'Database',
}

/** Users know these by what they do, not by the daemon's name. */
export function serviceLabel(service: string): string {
  return SERVICE_LABELS[service.toLowerCase()] ?? service.replace(/^\w/, (c) => c.toUpperCase())
}

const EVENT_LABELS: Record<string, { title: string; description: string }> = {
  'request.approved': { title: 'Request approved', description: 'An admin approved your request.' },
  'request.declined': { title: 'Request declined', description: 'An admin declined your request.' },
  'request.downloading': {
    title: 'Download started',
    description: 'Something you asked for started downloading.',
  },
  'request.available': {
    title: 'Ready to watch',
    description: 'Your request finished and is on the server.',
  },
  'request.failed': {
    title: 'Download failed',
    description: 'A download errored out and needs attention.',
  },
  'account.expiring': {
    title: 'Account expiring',
    description: 'A reminder before your access ends.',
  },
  'account.disabled': { title: 'Account disabled', description: 'Your access was turned off.' },
  'invite.redeemed': {
    title: 'Invite used',
    description: 'Someone signed up with one of your invite links.',
  },
  announcement: { title: 'Announcements', description: 'Server news and downtime notices.' },
}

export function eventLabel(event: string): { title: string; description: string } {
  return EVENT_LABELS[event] ?? { title: event, description: '' }
}
